import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore'; // Added updateDoc import
import { db } from '../firebase/firebaseConfig'; // Corrected path
import { useNavigate } from 'react-router-dom';
import { FFmpeg } from '@ffmpeg/ffmpeg'; // Correct import
const ffmpeg = new FFmpeg({ log: true }); // Correct initialization

const hexToRGBA = (hex, opacity) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const SlideShow = () => {
  const { slideshowId } = useParams();
  const [slides, setSlides] = useState([]);
  const [slideshows, setSlideshows] = useState([]); // State for gallery
  const [currentIndex, setCurrentIndex] = useState(0);
  const [bpm, setBpm] = useState(60); // Default BPM
  const [meta, setMeta] = useState({}); // Add state for slideshow metadata
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const [audioError, setAudioError] = useState(null);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false); // Add flag for auto-play
  const [volume, setVolume] = useState(1); // Default volume to 1 (max)
  const [isFading, setIsFading] = useState(false); // State for fade effect
  const [scale, setScale] = useState(100); // Default 100%
  const [completedSlideshows, setCompletedSlideshows] = useState({}); // Track completed slideshows

  const msPerBeat = 60000 / bpm;

  const queryParams = new URLSearchParams(window.location.search);
  const playMode = queryParams.get("play"); // 'slideshow', 'music', 'both'

  const getAspectRatioPadding = (aspectRatio) => {
    switch (aspectRatio) {
      case "16:9":
        return "56.25%";
      case "9:16":
        return "177.78%";
      case "4:3":
        return "75%";
      default:
        return "75%"; // Default to 4:3
    }
  };

  useEffect(() => {
    if (!meta.audioUrl) {
      console.warn("No audio URL found in slideshow metadata. Skipping audio-related logic.");
      return; // Exit early if audioUrl is missing
    }

    const fetchSlides = async () => {
      const q = query(
        collection(db, `slideshows/${slideshowId}/slides`),
        orderBy('timestamp') // Order slides by timestamp
      );
      const querySnapshot = await getDocs(q);
      const fetchedSlides = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSlides(fetchedSlides);
    };

    fetchSlides();

    // Re-fetch when audio is reloaded
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener("loadeddata", fetchSlides);
      return () => {
        audio.removeEventListener("loadeddata", fetchSlides);
      };
    }
  }, [slideshowId, meta.audioUrl]);

  useEffect(() => {
    const fetchSlideshows = async () => {
      const snapshot = await getDocs(collection(db, 'slideshows'));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSlideshows(list);
    };
    fetchSlideshows();
  }, []);

  useEffect(() => {
    const fetchMeta = async () => {
      const docRef = doc(db, `slideshows/${slideshowId}`);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setMeta(data);
        if (data.bpm) setBpm(data.bpm);
      }
    };
    fetchMeta();
  }, [slideshowId]);

  useEffect(() => {
    if (playMode === 'music' || playMode === 'both') {
      setShouldAutoPlay(true); // Wait for user action
    }
    // Remove automatic slideshow playback
    if (playMode === 'slideshow' || playMode === 'both') {
      // Ensure slideshow does not start automatically
    }

    return () => {
      audioRef.current?.pause();
    };
  }, [playMode]);

  useEffect(() => {
    if (!audioRef.current || slides.length === 0) return;

    const updateSlideByTime = () => {
      const currentTime = audioRef.current.currentTime;
      for (let i = 0; i < slides.length; i++) {
        const thisTime = slides[i].timestamp ?? 0;
        const nextTime = slides[i + 1]?.timestamp ?? Infinity;

        if (currentTime >= thisTime && currentTime < nextTime) {
          setCurrentIndex(i);
          setIsFading(true);
          setTimeout(() => setIsFading(false), 500); // Trigger fade effect
          break;
        }
      }
    };

    const interval = setInterval(updateSlideByTime, 100);
    return () => clearInterval(interval);
  }, [slides]);

  const handlePlay = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(error => {
        console.error("Audio play error:", error);
        setAudioError("Error playing audio: " + error.message);
      });
    }

    // Play the video manually as well (if current slide is a video)
    const currentSlide = slides[currentIndex];
    const videoEl = document.querySelector('video');

    if (currentSlide?.type === 'video' && videoEl) {
      videoEl.currentTime = 0;
      videoEl.play().catch((err) => {
        console.warn("Video play error:", err);
      });
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const stopSlideShow = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentIndex(0);
    }
  };

  const handleProgressClick = (index) => {
    setCurrentIndex(index);
    if (audioRef.current) {
      const newTime = (index / slides.length) * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
    }
  };

  const handleDeleteSlide = async (slideId) => {
    if (!window.confirm("Delete this slide?")) return;
    await deleteDoc(doc(db, `slideshows/${slideshowId}/slides`, slideId));
    setSlides(slides.filter((slide) => slide.id !== slideId));
  };

  const handleDeleteSlideshow = async (slideshowId) => {
    if (!window.confirm("Are you sure you want to delete this slideshow? This action cannot be undone.")) {
      return;
    }

    try {
      // Delete all slides in the slideshow
      const slidesCollection = collection(db, `slideshows/${slideshowId}/slides`);
      const slidesSnapshot = await getDocs(slidesCollection);
      const deleteSlidePromises = slidesSnapshot.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(deleteSlidePromises);

      // Delete the slideshow document
      await deleteDoc(doc(db, `slideshows/${slideshowId}`));

      // Remove the slideshow from the local state
      setSlideshows((prev) => prev.filter((show) => show.id !== slideshowId));

      alert("Slideshow deleted successfully!");
    } catch (error) {
      console.error("Error deleting slideshow:", error);
      alert("Failed to delete slideshow. Please try again.");
    }
  };

  // Commenting out handleDownload function
  /*
  const handleDownload = async (slideshowId) => {
    try {
      // Ensure FFmpeg is loaded
      if (!ffmpeg.isLoaded()) {
        await ffmpeg.load();
      }

      // 1. Load and write images to FFmpeg FS
      for (let i = 0; i < slides.length; i++) {
        console.log(`Downloading slide image: ${slides[i].imageUrl}`); // Log checkpoint
        const storagePath = decodeURIComponent(slides[i].imageUrl.split('/o/')[1].split('?')[0]); // Parse Firebase path
        const response = await fetch(slides[i].imageUrl);
        if (!response.ok) {
          throw new Error(`Image fetch failed: ${slides[i].imageUrl}`);
        }
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        ffmpeg.FS('writeFile', `img${i}.jpg`, new Uint8Array(arrayBuffer));
      }

      // 2. Write audio file to FFmpeg FS
      if (meta.audioUrl) {
        console.log(`Downloading audio file: ${meta.audioUrl}`); // Log checkpoint
        const audioResponse = await fetch(meta.audioUrl);
        if (!audioResponse.ok) {
          throw new Error(`Audio fetch failed: ${meta.audioUrl}`);
        }
        const audioBlob = await audioResponse.blob();
        const audioArrayBuffer = await audioBlob.arrayBuffer();
        ffmpeg.FS('writeFile', 'audio.mp3', new Uint8Array(audioArrayBuffer));
      }

      // 3. Generate input.txt for slide timing (using BPM)
      const beatsPerSlide = meta.beatsPerSlide || 8;
      const duration = (60000 / bpm) * beatsPerSlide / 1000; // Duration in seconds
      let inputTxt = '';
      slides.forEach((img, i) => {
        const text = img.caption ? img.caption.replace(/'/g, "\\'") : ' '; // Handle empty captions
        inputTxt += `file 'img${i}.jpg'\nduration ${duration}\n`;
      });
      inputTxt += `file 'img${slides.length - 1}.jpg'\n`; // Last frame

      console.log(`Writing input.txt`); // Log checkpoint
      ffmpeg.FS('writeFile', 'input.txt', inputTxt);

      // 4. Generate the slideshow video
      console.log(`Starting FFmpeg processing`); // Log checkpoint
      await ffmpeg.run(
        '-f', 'concat',
        '-safe', '0',
        '-i', 'input.txt',
        '-i', 'audio.mp3',
        '-vf', "scale=1280:720", // Force resolution
        '-shortest',
        '-c:v', 'libx264',
        '-c:a', 'aac',
        '-strict', 'experimental',
        'output.mp4'
      );

      // 5. Get the MP4 file and create a download link
      const data = ffmpeg.FS('readFile', 'output.mp4');
      const videoBlob = new Blob([data.buffer], { type: 'video/mp4' });
      const videoUrl = URL.createObjectURL(videoBlob);

      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `${slideshowId}.mp4`;
      link.click();
    } catch (error) {
      console.error('FFmpeg error:', error); // Improved error logging
      alert(`Failed to generate video: ${error.message}`);
    }
  };
  */

  const generateVideoServerSide = async (id) => {
    const targetId = id || slideshowId;
    try {
      console.log(`Starting server-side video generation for slideshowId: ${targetId}`); // Debugging log

      const response = await fetch(
        `https://us-central1-sbleducationalportal.cloudfunctions.net/generateSlideshowVideo?slideshowId=${targetId}`
      );

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();

      if (data.videoUrl) {
        console.log(`Video generated successfully: ${data.videoUrl}`); // Debugging log
        window.open(data.videoUrl, '_blank');
      } else {
        console.warn("Video generation failed. Response data:", data); // Improved logging
        alert("Video generation failed.");
      }
    } catch (error) {
      console.error("Error generating video:", error); // Improved error logging
      alert("An error occurred while generating the video. Please try again later.");
    }
  };

  const handleAudioEnded = () => {
    setCurrentIndex(0);
  };

  // Remove auto-play functionality
  useEffect(() => {
    const audioElement = document.querySelector('audio');
    if (audioElement) {
      audioElement.pause(); // Ensure no auto-play
    }
  }, []);

  // Function to handle volume change
  const handleVolumeChange = (event) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      console.log("Audio volume set to:", newVolume); // Debugging log
    }
  };

  // Check audio volume on mount
  useEffect(() => {
    if (audioRef.current) {
      console.log("Initial audio volume:", audioRef.current.volume);
      audioRef.current.volume = volume; // Ensure volume is set
    }
  }, [volume]);

  const toggleCompleted = async (id) => {
    const isCompleted = !completedSlideshows[id];
    setCompletedSlideshows((prev) => ({
      ...prev,
      [id]: isCompleted,
    }));

    try {
      const docRef = doc(db, `slideshows/${id}`);
      await updateDoc(docRef, { completed: isCompleted }); // Update Firestore
    } catch (error) {
      console.error("Error updating completed status:", error);
      alert("Failed to update completed status. Please try again.");
    }
  };

  useEffect(() => {
    const fetchCompletedStatus = async () => {
      const snapshot = await getDocs(collection(db, 'slideshows'));
      const completedStatus = {};
      snapshot.docs.forEach((doc) => {
        completedStatus[doc.id] = doc.data().completed || false;
      });
      setCompletedSlideshows(completedStatus);
    };

    fetchCompletedStatus();
  }, []);

  return (
    <div style={{ textAlign: 'center' }}>
      {/* Home and Navigation Buttons */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Home
        </button>
        
        <button
          onClick={() => navigate('/educator/slideuploader')}
          style={{
            backgroundColor: '#FF9800',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          SlideUploader
        </button>
        
      </div>

      <h2>{slideshowId}</h2>

      {/* Scale Dropdown */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{ marginRight: '10px' }}>Scale:</label>
        <select
          value={scale}
          onChange={(e) => setScale(parseInt(e.target.value))}
          style={{ padding: '5px 10px' }}
        >
          {[100, 90, 80, 70, 60, 50].map(val => (
            <option key={val} value={val}>{val}%</option>
          ))}
        </select>
      </div>

      {/* Independent Controls */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={handlePlay}
          style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Play
        </button>
        <button
          onClick={handlePause}
          style={{
            backgroundColor: '#FF9800',
            color: 'white',
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Pause
        </button>
        <button
          onClick={stopSlideShow}
          style={{
            backgroundColor: '#F44336',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Stop
        </button>
        <div style={{ marginTop: '10px' }}>
          <label htmlFor="volume-control" style={{ marginRight: '10px' }}>
            Volume:
          </label>
          <input
            id="volume-control"
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>

      {slides.length > 0 && slides[currentIndex]?.imageUrl ? (
        <div
          style={{
            position: "relative",
            width: `${scale}%`, // Use scale state
            paddingTop: getAspectRatioPadding(meta.aspectRatio),
            margin: "0 auto",
            backgroundColor: "#000",
            overflow: "hidden",
          }}
        >
          {slides[currentIndex]?.type === 'video' ? (
            <video
              ref={(el) => {
                if (el && playMode === 'both') {
                  el.currentTime = 0;
                  el.play().catch((err) => {
                    console.warn("Video play error:", err);
                  });
                }
              }}
              src={slides[currentIndex].imageUrl}
              muted
              loop
              playsInline
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          ) : (
            <img
              src={slides[currentIndex].imageUrl}
              alt={`Slide ${currentIndex + 1}`}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          )}
          {slides[currentIndex]?.caption && (
            <div
              style={{
                position: "absolute",
                bottom: slides[currentIndex]?.textPosition === 'top' ? 'auto' : '10%',
                top: slides[currentIndex]?.textPosition === 'top' ? '10%' : 'auto',
                left:
                  meta.textAlignment === "center"
                    ? "50%"
                    : meta.textAlignment === "right"
                    ? "auto"
                    : "5%",
                right: meta.textAlignment === "right" ? "5%" : "auto",
                transform:
                  meta.textAlignment === "center" ? "translateX(-50%)" : "none",
                backgroundColor: meta.textBackgroundEnabled
                  ? hexToRGBA(meta.textBackgroundColor || "#000", meta.textBackgroundOpacity || 0.5)
                  : "transparent",
                padding: meta.textBackgroundEnabled ? "10px" : "0",
                borderRadius: meta.textBackgroundEnabled ? "5px" : "0",
                color: meta.textColor || "#fff",
                fontFamily: meta.textFont || "Arial, sans-serif",
                fontSize: `${meta.textSize || 16}px`,
                textAlign: meta.textAlignment || "left",
                textShadow: "2px 2px 4px rgba(0, 0, 0, 0.7)",
                zIndex: 2,
                transition: "opacity 0.5s ease-in-out",
                opacity: isFading ? 0 : 1,
                maxWidth: "90%",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word", // Ensure text wraps correctly
              }}
            >
              {slides[currentIndex].caption}
            </div>
          )}
          {meta.logoUrl && (
            <img
              src={meta.logoUrl}
              alt="Logo"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                width: `${meta.logoSize || 10}%`,
                height: "auto",
                objectFit: "contain",
              }}
            />
          )}
        </div>
      ) : (
        <p>Loading or no slides found...</p>
      )}

      <audio
        ref={audioRef}
        src={meta.audioUrl || ''}
        preload="metadata" // Fallback for browsers
        controls // Add controls for debugging
        onEnded={handleAudioEnded}
      />

      {/* Generate Video Server-Side Button */}
      <div style={{ marginTop: '20px' }}>
        <button
          onClick={generateVideoServerSide}
          style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Download Slide w/ Music
        </button>
      </div>

      {/* Download Slide Pictures Button */}
      <button
        onClick={() => navigate(`/downloadSlidePictures/${slideshowId}`)}
        style={{
          backgroundColor: '#673ab7',
          color: 'white',
          padding: '10px 20px',
          marginTop: '20px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        Download Slide Pictures
      </button>

      {/* Slideshow Gallery */}
      <div style={{ padding: '20px', marginTop: '40px' }}>
        <h2>My Slideshows</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {slideshows.map(show => (
            <div
              key={show.id}
              style={{
                border: '1px solid #ccc',
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: completedSlideshows[show.id] ? '#d4edda' : 'white', // Green if completed
              }}
            >
              <h3>{show.title || show.id}</h3>
              <p><strong>BPM:</strong> {show.bpm || 'Not set'}</p>
              <p><strong>Audio:</strong> {show.audioUrl ? 'Uploaded' : 'Not uploaded'}</p>
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button onClick={() => navigate(`/slideshow/${show.id}?play=both`)}
                  style={{
                    backgroundColor: '#FDD835',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    marginTop: '10px',
                  }}>▶ Play Slideshow with Music</button>
                <button onClick={() => navigate(`/synceditor/${show.id}`)}
                  style={{
                    backgroundColor: '#2196F3',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    marginTop: '10px',
                  }}>✏️ Edit Slideshow</button>
                <button
                  onClick={() => handleDeleteSlideshow(show.id)}
                  style={{
                    backgroundColor: '#F44336',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    marginTop: '10px',
                  }}
                >
                  🗑️ Delete Slideshow
                </button>
                <label style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="checkbox"
                    checked={!!completedSlideshows[show.id]}
                    onChange={() => toggleCompleted(show.id)}
                  />
                  Completed
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SlideShow;
